import { useState, useRef, useCallback, useEffect } from 'react'

const POPUP_HIDE_DELAY = 500

const useClusterPopup = () => {
  const [popupData, setPopupData] = useState(null)
  const popupActiveRef = useRef(false)
  const timerRef = useRef(null)

  const hidePopup = useCallback(() => {
    popupActiveRef.current = false
    setPopupData(null)
  }, [])

  // Two leaves in a row -- one mark to the next, or off a mark and then off its popup -- would
  // otherwise leave the first timer running after the second replaced it, and the unmount below
  // clears only the one it holds: the orphan then hid the popup of a chart already gone.
  const scheduleHide = useCallback(hide => {
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(hide, POPUP_HIDE_DELAY)
  }, [])

  const handleShowClusterPopup = useCallback((clusterEl, cluster) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }

    setPopupData({ clusterEl, cluster })
  }, [])

  const handleClusterMouseLeave = useCallback(() => {
    scheduleHide(() => {
      if (popupActiveRef.current) return

      timerRef.current = null
      hidePopup()
    })
  }, [hidePopup, scheduleHide])

  const handlePopupMouseEnter = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    popupActiveRef.current = true
  }, [])

  const handlePopupMouseLeave = useCallback(() => {
    popupActiveRef.current = false

    scheduleHide(() => {
      timerRef.current = null
      hidePopup()
    })
  }, [hidePopup, scheduleHide])

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  return {
    popupData,
    handleShowClusterPopup,
    handleClusterMouseLeave,
    handlePopupMouseEnter,
    handlePopupMouseLeave
  }
}

export default useClusterPopup
